import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Table, message, Switch, Input, Radio, Tooltip, Popover, InputNumber, Button } from 'antd';
import { InfoCircleOutlined, SettingOutlined } from '@ant-design/icons';
import api from '../functions/api';
import epochToDate from '../functions/epochdate';
// DAS-2442: direction-aware coloring for the Shareprice (AE_AVG_abs_*) family.
import { sharepriceCellColor, spreadCellColor } from '../helpers/alertColors';
// DAS-2871 step 3: verdict view — one question, no columns to interpret.
import AlertHurtingTab from './AlertHurtingTab';
import ClauseScoreboard from './ClauseScoreboard'; // DAS-2977
import AlertTooEarlyTab from './AlertTooEarlyTab';
import {
  fetchAlertHurting,
  fetchDetailedAlertRecords,
  fetchAlerts,
  fetchAlertEffectiveness,
  fetchAlertSubtypeAdvisory,
  fetchConfidenceCorrelation,
  fetchAlertFollowThrough,
  // DAS-2356: user-adjustable advisory thresholds (n, %)
  readAdvisoryThresholds,
  writeAdvisoryThresholds,
  DAS2356_DEFAULT_THRESHOLD_PCT,
  DAS2356_DEFAULT_THRESHOLD_N,
} from "../functions/alertClauseService";
// AlertView no longer needed - using MarketAlerts instead
import MarketAlerts from './MarketAlerts'; // Import MarketAlerts component
import AlertFollowupProposals from './AlertFollowupProposals'; // DAS-2642: confirm/reject auto-detected follow-ups


const AlertEffectiveness = ({ isVisible, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailedData, setDetailedData] = useState([]);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [detailModalTitle, setDetailModalTitle] = useState('');
  const [clickedColumnName, setClickedColumnName] = useState('');
  const [clickedTargetColumn, setClickedTargetColumn] = useState(''); // DAS-2283: raw target column for price lookup
  // AlertView state variables removed - using MarketAlerts instead
  const [alerts, setAlerts] = useState([]); 
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [isMarketAlertsVisible, setIsMarketAlertsVisible] = useState(false); // State for MarketAlerts modal
  const [selectedAlertIdentifier, setSelectedAlertIdentifier] = useState(null); // Selected alert identifier for MarketAlerts
  const [showActiveOnly, setShowActiveOnly] = useState(false); // State for active-only filter
  const [searchText, setSearchText] = useState(''); // DAS-2092: Search by subtype name or identifier
  const [metricView, setMetricView] = useState('absolute'); // DAS-2281: which metric group to show
  const [cleanView, setCleanView] = useState(false); // DAS-2346: false = raw (outliers included, current behavior); true = clean (outliers excluded)
  // DAS-2347: regime filter. 'All' shows the regime-agnostic Alert_Effectiveness rows
  // (current behavior). BULL/BEAR/NEUTRAL fetches Alert_Effectiveness_by_Regime and
  // shows only that regime's slice. The current market regime comes from the
  // portfolio reducer's Redux state (state.portfolio.data.regime.current,
  // populated by the portfolio fetcher). Used as the default once known; user
  // can override at any time without re-snapping.
  const [regimeFilter, setRegimeFilter] = useState('All');
  // DAS-2871 finding 4: the modal used to auto-snap the regime filter to the
  // current market regime on open (DAS-2347). But the by-regime table is
  // missing 24 columns — all 16 hit rates, the medians, the DeGiro returns —
  // so auto-selecting a regime made "large parts of the screen silently blank"
  // the OPENING state. The panel now always opens on All; picking a regime is
  // a deliberate act. (The missing columns themselves are a separate, larger
  // fix — this only stops the panel defaulting into the broken view.)
  const currentRegime = useSelector(
    (state) => state?.portfolio?.data?.regime?.current ?? null
  );
  // DAS-2642: owner id for the follow-up proposals panel (reactive — per DAS-2557/2620).
  const followupUserId = useSelector((state) => state?.auth?.userId ?? null);
  // DAS-2348: under-performing subtype advisory (rendered as a panel at the top of the modal).
  const [advisoryRows, setAdvisoryRows] = useState([]);
  // DAS-2871: verdict data for the "hurting" tab. Loaded once per modal open;
  // null means the fetch soft-failed and the tab says so rather than showing zeros.
  const [hurting, setHurting] = useState(null);
  const [hurtingLoading, setHurtingLoading] = useState(false);
  // DAS-2871: which of the two views the modal is showing. null = the
  // chooser. The verdict view must NOT live inside the overview's tab strip:
  // nested there it inherits the search box, regime buttons, Raw/Clean toggle
  // and three panels, and can never be the clean single-answer screen it is
  // meant to be. The two views share nothing but the modal frame.
  const [modalView, setModalView] = useState(null);
  // DAS-2871: which view the overview was reached FROM via an Effectiveness
  // button (null = entered normally). In that focused mode only the table
  // matters — the three analysis panels are noise for "show me this one
  // alert's numbers" — and back must return to the originating list.
  const [overviewOrigin, setOverviewOrigin] = useState(null);
  const [advisoryLoading, setAdvisoryLoading] = useState(false);
  const [advisoryCollapsed, setAdvisoryCollapsed] = useState(false);
  // DAS-2356: user-adjustable thresholds. Seed from localStorage (so a reload
  // restores the user's preferences), default to (-5, 20) per the API contract.
  // Both the panel-header inline display and the network fetch read the SAME
  // state — they can never disagree about what's being filtered.
  const initialThresholds = readAdvisoryThresholds();
  const [advisoryThresholdPct, setAdvisoryThresholdPct] = useState(initialThresholds.thresholdPct);
  const [advisoryThresholdN, setAdvisoryThresholdN] = useState(initialThresholds.thresholdN);
  // Draft values inside the popover (only committed on Apply)
  const [advisoryDraftPct, setAdvisoryDraftPct] = useState(initialThresholds.thresholdPct);
  const [advisoryDraftN, setAdvisoryDraftN] = useState(initialThresholds.thresholdN);
  const [advisorySettingsOpen, setAdvisorySettingsOpen] = useState(false);
  // DAS-2349: confidence × effectiveness correlation panel.
  // Populates progressively as archived alerts age into 6m+ realized data
  // (will be empty / very small for the first few months after deploy).
  const [correlation, setCorrelation] = useState(null);
  const [correlationCollapsed, setCorrelationCollapsed] = useState(false);
  // DAS-2350: Follow-Through Dashboard data (Activity Tracker + Hindsight).
  const [followThrough, setFollowThrough] = useState(null);
  const [followThroughCollapsed, setFollowThroughCollapsed] = useState(false);


  const fetchAllAlerts = async () => {
    setLoading(true);
    try {
      const allAlerts = await fetchAlerts();
      console.log("Fetched all alerts:", allAlerts);
      setAlerts(allAlerts); // Store all alerts data
    } catch (error) {
      console.error("Error fetching all alerts:", error);
      message.error("Failed to fetch alerts.");
    } finally {
      setLoading(false);
    }
  };

  const enrichEffectivenessData = (effectivenessData, alerts) => {
    console.log("Effectiveness Data:", effectivenessData);
    // console.log("Alerts Data:", alerts);
  
    return effectivenessData.map((record) => {
      // console.log("Processing Record:", record);
  
      const identifiers = record.alert_subtype_identifier
        ? record.alert_subtype_identifier.split(",").map((id) => id.trim())
        : [];
      // console.log("Identifiers:", identifiers);
  
      const matchingStatuses = identifiers.map((id) => {
        const alert = alerts.find((alert) => String(alert.ID) === id);
        // console.log(`Matching Alert for ID ${id}:`, alert);
        return alert ? alert.status : null;
      });
      
      const finalStatus = matchingStatuses.every((status) => status === 1) ? 1 : 0;
      
  
      return {
        ...record,
        status: finalStatus,
      };
    });
  };
  
  
  
  const loadAlertEffectiveness = async (overrides = {}) => {
    setLoading(true);
    try {
        // DAS-2347: pick endpoint mode based on the regime filter. 'All' uses the
        // regime-agnostic Alert_Effectiveness; specific regimes use the by-regime
        // table and the rows are post-filtered to just that regime here in JS.
        const effRegime = overrides.regimeFilter ?? regimeFilter;
        const useByRegime = effRegime !== 'All';
        const rawRows = await fetchAlertEffectiveness({ byRegime: useByRegime });

        const filteredRows = useByRegime
          ? rawRows.filter((r) => (r.regime || '').toUpperCase() === effRegime.toUpperCase())
          : rawRows;

        const allAlerts = await fetchAlerts();
        const enrichedData = enrichEffectivenessData(filteredRows, allAlerts);

        setData(enrichedData);

        // Calculate and set total sum of n_total
        const total = enrichedData.reduce((sum, record) => sum + (record.n_total || 0), 0);
        setTotalAlerts(total);
    } catch (error) {
        message.error("Failed to load alert effectiveness data.");
    } finally {
        setLoading(false);
    }
};

  useEffect(() => {
    // DAS-2871: always come back to the chooser, never to whichever view
    // happened to be open last time. Also reset the regime filter — the modal
    // must never reopen defaulted into the column-poor by-regime view.
    if (!isVisible && modalView !== null) {
      setModalView(null);
      setOverviewOrigin(null);
    }
    if (!isVisible && regimeFilter !== 'All') {
      setRegimeFilter('All');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);



  useEffect(() => {
    if (isVisible) {
      fetchAllAlerts(); // Fetch all alerts
      loadAlertEffectiveness(); // Fetch alert effectiveness data
      setSearchText(''); // DAS-2092: Reset search on open
      // DAS-2348 / DAS-2356: load the under-performing-subtype advisory using
      // the user's saved thresholds (defaults: -5%, n>=20).
      setAdvisoryLoading(true);
      fetchAlertSubtypeAdvisory({
        thresholdPct: advisoryThresholdPct,
        thresholdN: advisoryThresholdN,
      })
        .then((rows) => setAdvisoryRows(rows))
        .finally(() => setAdvisoryLoading(false));
      // DAS-2349: load the confidence × effectiveness correlation
      fetchConfidenceCorrelation().then((data) => setCorrelation(data));
      // DAS-2871: load the verdict data for the "hurting" tab.
      setHurtingLoading(true);
      fetchAlertHurting()
        .then(setHurting)
        .finally(() => setHurtingLoading(false));

      // DAS-2350: load the Follow-Through Dashboard data
      fetchAlertFollowThrough().then((data) => setFollowThrough(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // DAS-2356: refetch the advisory when the user applies new thresholds
  // (skips the initial mount; that's covered by the isVisible effect above).
  useEffect(() => {
    if (!isVisible) return;
    setAdvisoryLoading(true);
    fetchAlertSubtypeAdvisory({
      thresholdPct: advisoryThresholdPct,
      thresholdN: advisoryThresholdN,
    })
      .then((rows) => setAdvisoryRows(rows))
      .finally(() => setAdvisoryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisoryThresholdPct, advisoryThresholdN]);

  // DAS-2347: re-fetch when the regime filter changes (skips the open-modal load)
  useEffect(() => {
    if (isVisible) {
      loadAlertEffectiveness();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regimeFilter]);

  // Handle Identifier click - open MarketAlerts instead of AlertView
  const handleIdentifierClick = (identifier) => {
    setSelectedAlertIdentifier(identifier);
    setIsMarketAlertsVisible(true);
  };
  
  // Helper function to round numbers to 2 decimal places
  const roundToTwoDecimals = (num) => {
    if (num === null || num === undefined) return null;
    return parseFloat(num).toFixed(2);
  };

  const fetchRecordDetails = async (record, targetColumn, columnName) => {
    setLoading(true);
    try {
      const details = await fetchDetailedAlertRecords(
        record.userid,
        record.alert_subtype_identifier,
        record.alert_type,
        record.alert_subtype,
        targetColumn
      );
      console.log(`Detailed records for ${targetColumn}:`, details);
      message.success(`Detailed records for ${targetColumn} fetched successfully!`);
  
      // Set clicked column name, target column, and modal title
      setClickedColumnName(columnName);
      setClickedTargetColumn(targetColumn); // DAS-2283: store raw target column for price field mapping
      setDetailModalTitle(
        `Detailed Records for ${record.alert_subtype_identifier}, ${record.alert_type}, ${record.alert_subtype}, user ${record.userid}`
      );
  
      // Show detailed records in the modal
      setDetailedData(details.map(item => ({
        ...item,
        target_column: item[targetColumn] || '-', // Add the target column dynamically
      })));
      setIsDetailModalVisible(true); // Open the detail modal
    } catch (error) {
      console.error('Error fetching detailed records:', error);
      message.error('Failed to fetch detailed records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open MarketAlerts modal instead of AlertView
  const handleOpenMarketAlerts = (identifier) => {
    setSelectedAlertIdentifier(identifier);
    setIsMarketAlertsVisible(true);
  };

  const handleCloseMarketAlerts = () => {
    setIsMarketAlertsVisible(false);
    setSelectedAlertIdentifier(null);
  };

  // Filter data based on active-only switch and search text (DAS-2092)
  const filteredData = data.filter(record => {
    if (showActiveOnly && record.status !== 1) return false;
    if (searchText.trim()) {
      const term = searchText.trim().toLowerCase();
      const subtype = (record.alert_subtype || '').toLowerCase();
      const identifier = (record.alert_subtype_identifier || '').toLowerCase();
      const alertType = (record.alert_type || '').toLowerCase();
      if (!subtype.includes(term) && !identifier.includes(term) && !alertType.includes(term)) {
        return false;
      }
    }
    return true;
  });

  // DAS-2547: how many of the currently-visible rows actually have outliers.
  // The Clean toggle is a no-op when this is 0, so we disable it (and explain why)
  // rather than letting it silently change nothing.
  const outlierRowCount = filteredData.filter(r => Number(r?.n_outliers) > 0).length;
  const noOutliersInView = outlierRowCount === 0;


    // DAS-2283: Map clicked target column to interval and price field
    const getIntervalFromColumn = (targetCol) => {
      if (!targetCol) return null;
      if (targetCol.includes('1w')) return { key: '1w', days: 7, priceField: 'price_at_1w' };
      if (targetCol.includes('1m')) return { key: '1m', days: 30, priceField: 'price_at_1m' };
      if (targetCol.includes('6m')) return { key: '6m', days: 180, priceField: 'price_at_6m' };
      if (targetCol.includes('1y')) return { key: '1y', days: 365, priceField: 'price_at_1y' };
      return null;
    };

    // Define the detailed modal table columns
    const detailColumns = [
      { title: 'ID', dataIndex: 'ID', key: 'ID' },
      {
        title: "Identifier",
        dataIndex: "alert_subtype_identifier",
        key: "alert_subtype_identifier",
        render: (text) => (
          <span
            style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleIdentifierClick(text)} // Call the handler on click
          >
            {text}
          </span>
        ),
      },
      { title: 'Alert Type', dataIndex: 'alert_type', key: 'alert_type' },
      { title: 'Alert Subtype', dataIndex: 'alert_subtype', key: 'alert_subtype' },
      {
  title: clickedColumnName,
  dataIndex: 'target_column',
  key: 'target_column',
  render: (text, record) => {
    let color = 'inherit';

    // Apply coloring logic based on the clicked column
    // DAS-2282: vs-X columns — backend already sign-flipped per alert type, raw sign rule works.
    // DAS-2442: Absolute AE columns — direction-aware per alert type (Harvest/Sell → falling stock = green).
    if (clickedColumnName.includes('AE against P') || clickedColumnName.includes('AE against DG') || clickedColumnName.includes('AE vs SP500')) {
      color = spreadCellColor(text);
    } else if (clickedColumnName.includes('Absolute AE')) {
      color = sharepriceCellColor(text, record?.alert_type);
    }

    // Outlier warning icon + tooltip
    const isOutlier = record.outlier === 1 || record.outlier === true;
    return (
      <span style={{ color, position: 'relative' }}>
        {text !== null && text !== undefined ? roundToTwoDecimals(text) : '-'}
        {isOutlier && (
          <span
            style={{
              marginLeft: 8,
              color: '#faad14',
              cursor: 'pointer',
              fontSize: 16,
              verticalAlign: 'middle',
            }}
            title="Potential outlier value"
          >
            ⚠️
          </span>
        )}
      </span>
    );
  },
},

      
      
      // { title: clickedColumnName, dataIndex: 'target_column', key: 'target_column' }, // Use clicked column name
      { title: 'Ticker', dataIndex: 'ticker', key: 'ticker' },
      {
        title: 'Price at Initiation',
        dataIndex: 'price_at_initiation',
        key: 'price_at_initiation',
        render: (text, record) => (
          <>
            <span>{text || '-'}</span>
            <br />
            <span style={{ color: 'gray' }}>{epochToDate(record.epoch_initiation)}</span>
          </>
        ),
      },
      {
        // DAS-2283: Price for Calculation — shows the price at the interval date
        title: (() => {
          const interval = getIntervalFromColumn(clickedTargetColumn);
          return interval ? `Price at +${interval.key}` : 'Price for Calc';
        })(),
        key: 'price_for_calculation',
        render: (_, record) => {
          const interval = getIntervalFromColumn(clickedTargetColumn);
          if (!interval) return '-';
          const price = record[interval.priceField];
          if (price === null || price === undefined) return '-';
          const targetEpoch = record.epoch_initiation + (interval.days * 86400);
          return (
            <>
              <span>{parseFloat(price).toFixed(2)}</span>
              <br />
              <span style={{ color: 'gray' }}>{epochToDate(targetEpoch)}</span>
            </>
          );
        },
      },
      {
        title: 'Price Current',
        dataIndex: 'price_current',
        key: 'price_current',
        render: (text, record) => (
          <>
            <span>{text || '-'}</span>
            <br />
            {/* <span style={{ color: 'gray' }}>{epochToDate(record.epoch)}</span> */}
          </>
        ),
      },
      {
        title: 'Alert Text',
        dataIndex: 'pf_alert_txt',
        key: 'pf_alert_txt',
        render: (text) => (
          <span style={{ whiteSpace: 'pre-wrap', fontSize: '10px' }}>{text || '-'}</span>
        ),
      },
      
    ];
  
  
  
  

  // DAS-2281: Helper to build a standard AE metric column (avg + clickable count)
  // DAS-2442: `isShareprice` flag routes Abs (AE_AVG_abs_*) columns through the
  // direction-aware colour helper so a falling stock on a Harvest alert is shown
  // green (alert was right). vs-X columns keep the raw sign rule because the
  // backend (DAS-2332) already sign-flipped them per alert type.
  // DAS-2871 step 1 audit, finding 8: every sorter did parseFloat(x || 0), so
  // "never measured" sorted exactly like a measured 0% — and 131 of 259 rows
  // have no 6-month S&P hit rate at all. antd passes the sort direction as the
  // comparator's third argument, which lets missing values be pushed to the
  // visual END whichever way the user sorts (antd negates the comparator for
  // descend, hence the sign flip).
  const numSorter = (field) => (a, b, sortOrder) => {
    const av = a[field] === null || a[field] === undefined || a[field] === '' ? NaN : parseFloat(a[field]);
    const bv = b[field] === null || b[field] === undefined || b[field] === '' ? NaN : parseFloat(b[field]);
    const aMiss = Number.isNaN(av);
    const bMiss = Number.isNaN(bv);
    if (aMiss && bMiss) return 0;
    const last = sortOrder === 'descend' ? -1 : 1;
    if (aMiss) return last;
    if (bMiss) return -last;
    return av - bv;
  };

  const aeMetricCol = (title, dataIndex, targetColumn, columnName, nField, isShareprice = false) => ({
    title,
    dataIndex,
    key: dataIndex,
    sorter: numSorter(dataIndex),
    render: (text, record) => (
      <>
        <span style={{
          color: isShareprice
            ? sharepriceCellColor(text, record?.alert_type)
            : spreadCellColor(text),
        }}>
          {roundToTwoDecimals(text) || '-'}
        </span>
        <br />
        <span
          style={{ color: 'gray', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => fetchRecordDetails(record, targetColumn, columnName)}
        >
          ({record[nField] || '-'})
        </span>
      </>
    ),
  });

  // DAS-2346: when cleanView is on, swap the average column and its count column
  // to the _clean variants populated by the second aggregation pass in
  // DR_Alert_Effectiveness_update.py. The targetColumn (used by the row-detail
  // modal to look up individual prices) is unchanged — that's about which underlying
  // archive column to read for per-row drill-down, not about the aggregated value.
  const F = (base) => (cleanView ? `${base}_clean` : base);

  // --- Column groups ---
  // DAS-2442: pass isShareprice=true so Abs cells use the direction-aware
  // helper. Without this flag they would be coloured by raw sign — which is
  // misleading for Harvest/Sell alerts where a falling stock is the success
  // signal.
  const absoluteCols = [
    aeMetricCol('Abs 1w Avg', F('AE_AVG_abs_1w'), 'AE_1w_absolute_return', 'Absolute AE 1 Wk Avg', F('n_AE_AVG_abs_1w'), true),
    aeMetricCol('Abs 1m Avg', F('AE_AVG_abs_1m'), 'AE_1m_absolute_return', 'Absolute AE 1m Avg', F('n_AE_AVG_abs_1m'), true),
    aeMetricCol('Abs 6m Avg', F('AE_AVG_abs_6m'), 'AE_6m_absolute_return', 'Absolute AE 6m Avg', F('n_AE_AVG_abs_6m'), true),
    aeMetricCol('Abs 1y Avg', F('AE_AVG_abs_1y'), 'AE_1y_absolute_return', 'Absolute AE 1y Avg', F('n_AE_AVG_abs_1y'), true),
  ];

  const portfolioCols = [
    aeMetricCol('vs PF 1w', F('AE_AVG_P_1w'), 'AE_P_1w', 'AE against P 1 Wk Avg', F('n_AE_AVG_P_1w')),
    aeMetricCol('vs PF 1m', F('AE_AVG_P_1m'), 'AE_P_1m', 'AE against P 1m Avg', F('n_AE_AVG_P_1m')),
    aeMetricCol('vs PF 6m', F('AE_AVG_P_6m'), 'AE_P_6m', 'AE against P 6m Avg', F('n_AE_AVG_P_6m')),
    aeMetricCol('vs PF 1y', F('AE_AVG_P_1y'), 'AE_P_1y', 'AE against P 1y Avg', F('n_AE_AVG_P_1y')),
  ];

  const dgCols = [
    aeMetricCol('vs DG 1w', F('AE_DG_P_1w'), 'AE_DG_P_1w', 'AE against DG 1 Wk AVG', F('n_AE_DG_P_1w')),
    aeMetricCol('vs DG 1m', F('AE_DG_P_1m'), 'AE_DG_P_1m', 'AE against DG 1m AVG', F('n_AE_DG_P_1m')),
    aeMetricCol('vs DG 6m', F('AE_DG_P_6m'), 'AE_DG_P_6m', 'AE against DG 6m AVG', F('n_AE_DG_P_6m')),
    aeMetricCol('vs DG 1y', F('AE_DG_P_1y'), 'AE_DG_P_1y', 'AE against DG 1y AVG', F('n_AE_DG_P_1y')),
  ];

  const sp500Cols = [
    aeMetricCol('vs SP500 1w', F('AE_AVG_SP_1w'), 'AE_SP_1w', 'AE vs SP500 1w', F('n_AE_AVG_SP_1w')),
    aeMetricCol('vs SP500 1m', F('AE_AVG_SP_1m'), 'AE_SP_1m', 'AE vs SP500 1m', F('n_AE_AVG_SP_1m')),
    aeMetricCol('vs SP500 6m', F('AE_AVG_SP_6m'), 'AE_SP_6m', 'AE vs SP500 6m', F('n_AE_AVG_SP_6m')),
    aeMetricCol('vs SP500 1y', F('AE_AVG_SP_1y'), 'AE_SP_1y', 'AE vs SP500 1y', F('n_AE_AVG_SP_1y')),
  ];

  // DAS-2332: column header with a plain-English tooltip
  const colHeader = (label, tip) => (
    <span>
      {label}{' '}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help' }} />
      </Tooltip>
    </span>
  );

  // DAS-2871 finding 10: hit-rate cells showed a bare percentage — the exact
  // gap that produced DAS-2869, where a 100% built on ONE alert sat under a
  // subtype labelled "Alerts issued: 107". Every hit-rate cell now carries the
  // count it was computed from. The n-fields mirror the average columns':
  // both are computed from the same value lists in the backend rollup.
  // DAS-3054: `evidenceField` names the backend's evidence text for this
  // rate ("can judge" / "weak evidence" / "too little data — N more fires").
  // When present it is shown under the percentage, so a 70% on 20 fires is
  // never read as a record. Colour follows the tier field (same key without
  // the `_text` suffix).
  const evidenceColor = (tier) =>
    tier === 'can_judge' ? '#2c7a44' : tier === 'weak' ? '#b26a00' : '#8c8c8c';
  const hitRateCell = (nField, goodAt, badAt, evidenceField) => (text, record) => {
    if (text === null || text === undefined) return <span style={{ color: 'gray' }}>-</span>;
    const val = parseFloat(text);
    const color = badAt !== undefined
      ? (val >= goodAt ? 'green' : val <= badAt ? 'red' : 'orange')
      : (val >= goodAt ? 'green' : 'red');
    const evText = evidenceField ? record[evidenceField] : null;
    const evTier = evidenceField ? record[evidenceField.replace(/_text$/, '')] : null;
    return (
      <span style={{ color }}>
        {val.toFixed(1)}%
        <span style={{ color: '#999', fontSize: 11, marginLeft: 4 }}>({record[nField] ?? '-'})</span>
        {evText ? (
          <span
            data-testid="das3054-evidence"
            title="How much this rate can be trusted, from its fire count alone (80% power vs the pooled rate of all alert types)."
            style={{ display: 'block', fontSize: 10, color: evidenceColor(evTier), fontWeight: 500 }}
          >
            {evText}
          </span>
        ) : null}
      </span>
    );
  };

  const hitRateCols = [
    {
      title: colHeader(
        'HR Dir 1m',
        "% of alerts that moved in the direction predicted over 1 month. Accumulate: hit if price went up. Harvest: hit if price went down. Blank for Monitor/Step In — no expected direction. ≥60% green, ≤40% red."
      ),
      dataIndex: 'hit_rate_dir_1m',
      key: 'hit_rate_dir_1m',
      sorter: numSorter('hit_rate_dir_1m'),
      render: hitRateCell('n_AE_AVG_abs_1m', 60, 40),
    },
    {
      title: colHeader(
        'HR vs PF 1m',
        "% of alerts where following the signal (buy for Accumulate, sell for Harvest) beat just holding your full portfolio (Saxo + DeGiro + manual) over 1 month. ≥50% green."
      ),
      dataIndex: 'hit_rate_pf_1m',
      key: 'hit_rate_pf_1m',
      sorter: numSorter('hit_rate_pf_1m'),
      render: hitRateCell('n_AE_AVG_P_1m', 50),
    },
    {
      title: colHeader(
        'HR vs DG 1m',
        "Same as HR vs PF but compared only to your DeGiro account (not Saxo or manual positions)."
      ),
      dataIndex: 'hit_rate_dg_1m',
      key: 'hit_rate_dg_1m',
      sorter: numSorter('hit_rate_dg_1m'),
      render: hitRateCell('n_AE_DG_P_1m', 50),
    },
    // DAS-2548: S&P 500 hit ratio for all timeframes (1w / 1m / 6m / 1y).
    // % of alerts where following the signal beat holding SPY over each window.
    ...['1w', '1m', '6m', '1y'].map((tf) => ({
      title: colHeader(
        `HR vs SP ${tf}`,
        `% of alerts where following the signal (buy for Accumulate, sell for Harvest) beat just holding the S&P 500 (SPY) over the same ${tf === '1w' ? '1-week' : tf === '1m' ? '1-month' : tf === '6m' ? '6-month' : '1-year'} window. ≥50% green.`
      ),
      dataIndex: `hit_rate_sp_${tf}`,
      key: `hit_rate_sp_${tf}`,
      sorter: numSorter(`hit_rate_sp_${tf}`),
      render: hitRateCell(`n_AE_AVG_SP_${tf}`, 50),
    })),
    {
      title: colHeader(
        'Median 1m',
        "Middle value of all 1-month raw stock returns for this alert type. Half the alerts did better, half worse. Less distorted by one or two outliers than the average. Positive = stocks rose (good for Accumulate, bad for Harvest)."
      ),
      dataIndex: 'AE_MEDIAN_abs_1m',
      key: 'AE_MEDIAN_abs_1m',
      sorter: numSorter('AE_MEDIAN_abs_1m'),
      render: (text) => {
        if (text === null || text === undefined) return '-';
        const val = parseFloat(text);
        return <span style={{ color: val > 0 ? 'green' : val < 0 ? 'red' : 'inherit' }}>{val.toFixed(2)}</span>;
      },
    },
    {
      title: colHeader(
        'Info Ratio',
        "Average 1m return ÷ how much the returns jump around (volatility). Above +0.5 = reliably positive. Below −0.5 = reliably negative. Between = noisy. Sign should match the alert's intent: positive for Accumulate, negative for Harvest."
      ),
      dataIndex: 'info_ratio_1m',
      key: 'info_ratio_1m',
      sorter: numSorter('info_ratio_1m'),
      render: (text) => {
        if (text === null || text === undefined) return '-';
        const val = parseFloat(text);
        const color = val > 0.5 ? 'green' : val < -0.5 ? 'red' : 'inherit';
        return <span style={{ color }}>{val.toFixed(2)}</span>;
      },
    },
    {
      title: colHeader(
        'Signif.',
        "Is this alert's mean 1m return reliably different from zero? *** = <1% chance it's random. ** = <5%. * = <10%. ns = could be random. n<10 = not enough data. Green when direction of the mean matches alert intent (Accumulate+up, Harvest+down); red when it contradicts."
      ),
      key: 'significance',
      render: (_, record) => {
        const p = record.p_value_1m;
        if (p === null || p === undefined) return <span style={{ color: 'gray', fontSize: 11 }}>n&lt;10</span>;
        const pVal = parseFloat(p);
        let stars = '';
        if (pVal < 0.01) stars = '***';
        else if (pVal < 0.05) stars = '**';
        else if (pVal < 0.10) stars = '*';
        else stars = 'ns';

        // DAS-2332: color depends on whether the sign of mean return matches the alert's intent
        const at = (record.alert_type || '').toLowerCase();
        const mean = parseFloat(record.AE_AVG_abs_1m);
        let intentMatch = null;
        if (!isNaN(mean)) {
          if (at.includes('accumulate') || at.includes('buy')) intentMatch = mean > 0;
          else if (at.includes('harvest') || at.includes('sell')) intentMatch = mean < 0;
        }

        let color = 'gray';
        if (stars === 'ns') {
          color = 'gray';
        } else if (intentMatch === true) {
          color = pVal < 0.01 ? 'green' : pVal < 0.05 ? '#52c41a' : 'orange';
        } else if (intentMatch === false) {
          color = pVal < 0.01 ? '#cf1322' : pVal < 0.05 ? '#ff4d4f' : '#ff7875';
        }

        return (
          <span title={`t=${record.t_stat_1m}, p=${pVal.toFixed(4)}`} style={{ color, fontWeight: 'bold', cursor: 'help' }}>
            {stars}
          </span>
        );
      },
    },
  ];

  // DAS-2548: S&P 500 hit ratio (% beating SPY) for all timeframes, shown on the
  // "vs S&P 500" tab next to the average columns — the natural place to look for S&P
  // performance (the Hit Rates tab also carries them, but the ticket's view is this one).
  // NOTE: hit rates have no _clean variant, so do NOT wrap in F() — they are the same
  // under Raw/Clean (outlier toggle only affects the averages, per DAS-2346).
  const sp500HitRateCols = ['1w', '1m', '6m', '1y'].map((tf) => {
    const window = tf === '1w' ? '1-week' : tf === '1m' ? '1-month' : tf === '6m' ? '6-month' : '1-year';
    return {
      title: colHeader(
        `HR SP ${tf}`,
        `Hit ratio vs the S&P 500: % of this alert type's alerts that beat just holding SPY over the ${window} window. ≥50% green, else red.`
      ),
      dataIndex: `hit_rate_sp_${tf}`,
      key: `sp_hr_${tf}`,
      sorter: numSorter(`hit_rate_sp_${tf}`),
      // DAS-3054: the backend attaches evidence_sp_<tf> / evidence_sp_<tf>_text
      // per row. Shown under the 6-month rate only — the decision horizon —
      // so a 192-row table does not carry four labels per row.
      render: hitRateCell(`n_AE_AVG_SP_${tf}`, 50, undefined,
        tf === '6m' ? 'evidence_sp_6m_text' : undefined),
    };
  });

  // Map view name to column group
  const metricColumnGroups = {
    absolute: absoluteCols,
    portfolio: portfolioCols,
    degiro: dgCols,
    sp500: [...sp500Cols, ...sp500HitRateCols],
    hitrate: hitRateCols,
  };

  // Identity columns (always shown)
  const identityCols = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', sorter: (a, b) => a.ID - b.ID },
    {
      title: 'Identifier',
      dataIndex: 'alert_subtype_identifier',
      key: 'alert_subtype_identifier',
      render: (text) => (
        <span
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handleOpenMarketAlerts(text)}
        >
          {text}
        </span>
      ),
    },
    {
  title: 'Alert Type',
  dataIndex: 'alert_type',
  key: 'alert_type',
  sorter: (a, b) => a.alert_type.localeCompare(b.alert_type),
  render: (text, record) => {
    const out = record.n_outliers;
    return (
      <>
        <span>{text}</span>
        {Number(out) > 0 && (
          <span
            style={{
              marginLeft: 8,
              color: '#faad14',
              cursor: 'pointer',
              fontSize: 16,
              verticalAlign: 'middle',
            }}
            title={`${out} potential outliers detected`}
          >
            ⚠️ ({out})
          </span>
        )}
      </>
    );
  },
},

    {
    title: 'Alert Subtype',
    dataIndex: 'alert_subtype',
    key: 'alert_subtype',
    width: 200,
    sorter: (a, b) => a.alert_subtype.localeCompare(b.alert_subtype),
    render: (text, record) => (
      <div>
        <span>{text}</span>
        <br />
        <span style={{ fontStyle: 'italic', fontSize: '12px', color: 'gray' }}>
          Alerts issued: {record.n_total || '-'}
        </span>
      </div>
    ),
  },
  ];

  // Trailing columns (always shown after metrics)
  const trailingCols = [
    {
      title: 'n_total',
      dataIndex: 'n_total',
      key: 'n_total',
      sorter: (a, b) => (a.n_total || 0) - (b.n_total || 0),
      // DAS-2871 finding 12: the table opened in database-ID order. Most
      // evidence first is the one ordering that never lies about reliability.
      defaultSortOrder: 'descend',
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Status</span>
          <Switch
            size="small"
            checked={showActiveOnly}
            onChange={setShowActiveOnly}
            checkedChildren="Active"
            unCheckedChildren="All"
            title="Show only active records"
          />
        </div>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: status === 1 ? "green" : "red", fontWeight: "bold" }}>
          {status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  // DAS-2281: Assemble columns dynamically based on selected metric view
  // DAS-2346: cleanView added to deps so toggling rebuilds column field-name resolution
  const columns = useMemo(() => [
    ...identityCols,
    ...(metricColumnGroups[metricView] || absoluteCols),
    ...trailingCols,
  ], [metricView, showActiveOnly, alerts, cleanView]);

  
  return (
    <>
      <Modal
        // DAS-2871: the chooser and the verdict view get plain titles. The old
        // overview keeps its existing title, counts and all.
        title={
          modalView === null
            ? 'Alert Effectiveness'
            : modalView === 'hurting'
              ? 'Alerts that are hurting — 6-month portfolio performance'
            : modalView === 'working'
              ? 'Alerts that are working — 6-month portfolio performance'
            : modalView === 'sp_hurting'
              ? 'Alerts that are hurting — 6-month vs S&P 500'
            : modalView === 'sp_working'
              ? 'Alerts that are working — 6-month vs S&P 500'
            : modalView === 'tooearly'
              ? 'Too early to tell'
              : `Alert Effectiveness${regimeFilter !== 'All' ? ` — ${regimeFilter} regime` : ''} (${filteredData.length}${showActiveOnly ? ' active' : ''} alert types · ${totalAlerts} alerts behind them)`
        }
        visible={isVisible}
        onCancel={onClose}
        footer={null}
        width={1400}
    >

        {/* ================= DAS-2871: the chooser =========================
            The modal opens on a choice, not on a screen full of controls.
            Two entries, nothing else. Everything the panel used to show lives
            untouched behind "Full overview"; the verdict view is its own
            screen and inherits none of that furniture. */}
        {modalView === null && (
          <div style={{ maxWidth: 720, margin: '8px auto 24px' }}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 18 }}>
              What would you like to look at?
            </div>

            <div
              data-testid="ae-menu-hurting-portfolio"
              onClick={() => setModalView('hurting')}
              style={{
                border: '1px solid #f0c2bd', background: '#fdf4f3', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer', marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Alerts that are hurting — 6-month portfolio performance
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                Which alert types cost me money over 6 months, compared with ignoring them and
                simply holding my portfolio. One verdict per alert type, in words.
              </div>
            </div>

            <div
              data-testid="ae-menu-working-portfolio"
              onClick={() => setModalView('working')}
              style={{
                border: '1px solid #bfe3cb', background: '#f2faf5', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer', marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Alerts that are working — 6-month portfolio performance
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                Which alert types made me money over 6 months, compared with ignoring them and
                simply holding my portfolio. Same method as the hurting list, other tail.
              </div>
            </div>

            <div
              data-testid="ae-menu-hurting-sp500"
              onClick={() => setModalView('sp_hurting')}
              style={{
                border: '1px solid #f0c2bd', background: '#fdf4f3', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer', marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Alerts that are hurting — 6-month vs S&P 500
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                The same suspect ranking, measured against simply holding the S&P 500 instead of
                your portfolio. This baseline is unaffected by the portfolio-benchmark fault
                (DAS-2872), so it is the reliable one for now.
              </div>
            </div>

            <div
              data-testid="ae-menu-working-sp500"
              onClick={() => setModalView('sp_working')}
              style={{
                border: '1px solid #bfe3cb', background: '#f2faf5', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer', marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Alerts that are working — 6-month vs S&P 500
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                Which alert types beat simply holding the S&P 500 over 6 months. Sell alerts are
                measured direction-correctly here, unlike the overview's S&P columns (DAS-2870).
              </div>
            </div>

            <div
              data-testid="ae-menu-tooearly"
              onClick={() => setModalView('tooearly')}
              style={{
                border: '1px solid #dbe4ee', background: '#f5f8fc', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer', marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Too early to tell
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                The alert types that cannot be judged yet, and which ones are closest to their
                first verdict. No percentages — progress only.
              </div>
            </div>

            <div
              data-testid="ae-menu-clauses"
              onClick={() => setModalView('clauses')}
              style={{
                border: '1px solid #d9d0ee', background: '#f7f4fd', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer', marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Clause scoreboard — which ingredients earn their place
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                Per clause: how the alerts where it fired TRUE performed vs the S&P at 6 months.
                Evidence for future clause re-weighting (DAS-2950/2976/2977).
              </div>
            </div>

            <div
              data-testid="ae-menu-overview"
              onClick={() => {
                // Entering via the menu is the full experience: all panels,
                // no leftover filter from an earlier Effectiveness jump.
                setOverviewOrigin(null);
                setSearchText('');
                setModalView('overview');
              }}
              style={{
                border: '1px solid #e2e2e2', background: '#fafafa', borderRadius: 10,
                padding: '18px 20px', cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>
                Full overview
              </div>
              <div style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6 }}>
                The complete table — every alert type, every measure, every period, plus the
                under-performing, confidence and follow-through panels. Unchanged.
              </div>
            </div>
          </div>
        )}

        {/* ================= DAS-2871: the verdict view =====================
            Rendered alone. No search box, no regime buttons, no Raw/Clean
            toggle, no side panels — those belong to the overview. */}
        {/* DAS-2977: the clause scoreboard, rendered alone like the verdict views */}
        {modalView === 'clauses' && (
          <>
            <div
              onClick={() => setModalView(null)}
              style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer', marginBottom: 16 }}
            >
              ← back
            </div>
            <ClauseScoreboard />
          </>
        )}

        {modalView === 'hurting' && (
          <>
            <div
              onClick={() => setModalView(null)}
              style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer', marginBottom: 16 }}
            >
              ← back
            </div>
            <AlertHurtingTab
              data={hurting}
              loading={hurtingLoading}
              // Reuses the existing definitions editor rather than inventing a
              // second way to edit an alert.
              onEdit={handleOpenMarketAlerts}
              // After a switch-off the verdict list must not keep showing the
              // alert type it just retired.
              onChanged={() => {
                setHurtingLoading(true);
                fetchAlertHurting().then(setHurting).finally(() => setHurtingLoading(false));
                loadAlertEffectiveness();
              }}
              // DAS-2871: "Effectiveness" on a suspect jumps to the full
              // overview with the search box prefilled to that identifier, so
              // it lands on the alert's rows (incl. its combinations) rather
              // than on page 1 of 259.
              onShowEffectiveness={(identifier) => {
                setSearchText(String(identifier || ''));
                setOverviewOrigin('hurting');
                setModalView('overview');
              }}
            />
          </>
        )}

        {modalView === 'working' && (
          <>
            <div
              onClick={() => setModalView(null)}
              style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer', marginBottom: 16 }}
            >
              ← back
            </div>
            <AlertHurtingTab
              mode="working"
              data={hurting}
              loading={hurtingLoading}
              onEdit={handleOpenMarketAlerts}
              onShowEffectiveness={(identifier) => {
                setSearchText(String(identifier || ''));
                setOverviewOrigin('working');
                setModalView('overview');
              }}
            />
          </>
        )}

        {(modalView === 'sp_hurting' || modalView === 'sp_working') && (
          <>
            <div
              onClick={() => setModalView(null)}
              style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer', marginBottom: 16 }}
            >
              ← back
            </div>
            <AlertHurtingTab
              benchmark="sp500"
              mode={modalView === 'sp_working' ? 'working' : 'hurting'}
              data={hurting}
              loading={hurtingLoading}
              onEdit={handleOpenMarketAlerts}
              onChanged={() => {
                setHurtingLoading(true);
                fetchAlertHurting().then(setHurting).finally(() => setHurtingLoading(false));
                loadAlertEffectiveness();
              }}
              onShowEffectiveness={(identifier) => {
                setSearchText(String(identifier || ''));
                setOverviewOrigin(modalView);
                setModalView('overview');
              }}
            />
          </>
        )}

        {modalView === 'tooearly' && (
          <>
            <div
              onClick={() => setModalView(null)}
              style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer', marginBottom: 16 }}
            >
              ← back
            </div>
            <AlertTooEarlyTab
              data={hurting}
              loading={hurtingLoading}
              onEdit={handleOpenMarketAlerts}
              onShowEffectiveness={(identifier) => {
                setSearchText(String(identifier || ''));
                setOverviewOrigin('tooearly');
                setModalView('overview');
              }}
            />
          </>
        )}

        {/* ================= the existing overview, untouched =============== */}
        {modalView === 'overview' && (
          <>
        <div
          onClick={() => {
            if (overviewOrigin) {
              const origin = overviewOrigin;
              setOverviewOrigin(null);
              setSearchText('');
              setModalView(origin);
            } else {
              setModalView(null);
            }
          }}
          style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer', marginBottom: 12 }}
        >
          {overviewOrigin === 'hurting' || overviewOrigin === 'sp_hurting' ? '← back to suspects'
            : overviewOrigin === 'working' || overviewOrigin === 'sp_working' ? '← back to the working list'
            : overviewOrigin === 'tooearly' ? '← back to too-early list'
            : '← back'}
        </div>

        {/* DAS-2642: auto-detected follow-up proposals — confirm/reject (self-hides when none). */}
        {!overviewOrigin && (
          <AlertFollowupProposals userId={followupUserId} isVisible={isVisible} />
        )}

        {/* DAS-2348 / DAS-2356: Under-performing-subtype advisory panel.
            Shows subtypes that meet AE_AVG_P_6m_clean < threshold_pct AND
            n_clean >= threshold_n (defaults -5% / 20, user-adjustable via
            DAS-2356 settings popover). The current-regime cell is highlighted
            using the Redux-derived currentRegime; if currentRegime is null we
            fall back to NEUTRAL. Panel is hidden only when at DEFAULT thresholds
            AND nothing is flagged — once the user customizes thresholds, the
            panel stays visible so they can adjust further. */}
        {!overviewOrigin && (() => {
          const usingDefaults =
            advisoryThresholdPct === DAS2356_DEFAULT_THRESHOLD_PCT &&
            advisoryThresholdN === DAS2356_DEFAULT_THRESHOLD_N;
          if (usingDefaults && advisoryRows.length === 0) return null;
          const cur = (currentRegime || 'NEUTRAL').toUpperCase();
          const curValKey = `ae_p_6m_clean_${cur}`;
          const curNKey   = `n_p_6m_clean_${cur}`;
          const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${parseFloat(v).toFixed(1)}%`;
          const fmtN   = (v) => (v === null || v === undefined) ? '—' : v;
          const suggestedAction = (r) => {
            const cv = r[curValKey];
            const cn = r[curNKey];
            if (cn === null || cn === undefined || cn < 10) return 'Insufficient current-regime data; review';
            const cvf = parseFloat(cv);
            if (Number.isNaN(cvf)) return 'Insufficient current-regime data; review';
            if (cvf < advisoryThresholdPct) return 'Review or archive';
            return 'Regime-conditional; review BULL/BEAR/NEUTRAL split before disabling';
          };

          // DAS-2356: settings popover body. Edits a DRAFT pair; Apply commits.
          const settingsContent = (
            <div style={{ width: 240 }}>
              <div style={{ marginBottom: 8, fontSize: 12, color: '#555' }}>
                Flag a subtype when{' '}
                <strong>AE_P_6m_clean &lt; {advisoryDraftPct}%</strong>{' '}
                AND <strong>n &ge; {advisoryDraftN}</strong>.
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Max acceptable % (−100 &lt; pct ≤ 0)</div>
                <InputNumber
                  value={advisoryDraftPct}
                  onChange={(v) => setAdvisoryDraftPct(v)}
                  min={-99.99} max={0} step={0.5} style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Min sample size (1 ≤ n ≤ 1000)</div>
                <InputNumber
                  value={advisoryDraftN}
                  onChange={(v) => setAdvisoryDraftN(v)}
                  min={1} max={1000} step={1} style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <a
                  href="#"
                  style={{ fontSize: 11 }}
                  onClick={(e) => {
                    e.preventDefault();
                    setAdvisoryDraftPct(DAS2356_DEFAULT_THRESHOLD_PCT);
                    setAdvisoryDraftN(DAS2356_DEFAULT_THRESHOLD_N);
                    writeAdvisoryThresholds({ thresholdPct: null, thresholdN: null });
                    setAdvisoryThresholdPct(DAS2356_DEFAULT_THRESHOLD_PCT);
                    setAdvisoryThresholdN(DAS2356_DEFAULT_THRESHOLD_N);
                    setAdvisorySettingsOpen(false);
                  }}
                >Reset to defaults</a>
                <Button
                  type="primary"
                  size="small"
                  disabled={
                    advisoryDraftPct === null || advisoryDraftN === null ||
                    !(advisoryDraftPct > -100 && advisoryDraftPct <= 0) ||
                    !(advisoryDraftN >= 1 && advisoryDraftN <= 1000)
                  }
                  onClick={() => {
                    writeAdvisoryThresholds({
                      thresholdPct: advisoryDraftPct,
                      thresholdN: advisoryDraftN,
                    });
                    setAdvisoryThresholdPct(advisoryDraftPct);
                    setAdvisoryThresholdN(advisoryDraftN);
                    setAdvisorySettingsOpen(false);
                  }}
                >Apply</Button>
              </div>
            </div>
          );

          return (
            <div style={{
              marginBottom: 16,
              padding: 12,
              border: '1px solid #ffccc7',
              borderRadius: 4,
              background: '#fff2f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 'bold', color: '#cf1322' }}>
                  ⚠️ Under-performing subtypes ({advisoryRows.length}) — current regime: {cur}
                  <Tooltip title={`Subtypes whose alerts have, on average, lost more than ${Math.abs(advisoryThresholdPct)}% over 6 months vs. holding the portfolio (clean averages, n ≥ ${advisoryThresholdN}). Click 'Edit' to disable manually if appropriate. Never auto-disabled. — DAS-2348/2356`}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help', marginLeft: 6 }} />
                  </Tooltip>
                  {/* DAS-2356: inline display of active thresholds — what you see is what's being filtered */}
                  <span style={{ fontWeight: 'normal', color: '#8c2628', fontSize: 11, marginLeft: 10 }}>
                    flagging AE_P_6m_clean &lt; {advisoryThresholdPct}% AND n ≥ {advisoryThresholdN}
                  </span>
                  <Popover
                    open={advisorySettingsOpen}
                    onOpenChange={(open) => {
                      // re-seed draft from committed when opening
                      if (open) {
                        setAdvisoryDraftPct(advisoryThresholdPct);
                        setAdvisoryDraftN(advisoryThresholdN);
                      }
                      setAdvisorySettingsOpen(open);
                    }}
                    content={settingsContent}
                    title="Advisory thresholds (DAS-2356)"
                    trigger="click"
                    placement="bottomLeft"
                  >
                    <SettingOutlined
                      title="Adjust thresholds"
                      style={{ color: '#1890ff', fontSize: 12, cursor: 'pointer', marginLeft: 8 }}
                    />
                  </Popover>
                </div>
                <Switch
                  size="small"
                  checked={!advisoryCollapsed}
                  onChange={(checked) => setAdvisoryCollapsed(!checked)}
                  checkedChildren="Show"
                  unCheckedChildren="Hide"
                />
              </div>
              {/* DAS-2356: empty-state when the user's thresholds match nothing */}
              {advisoryRows.length === 0 && !advisoryCollapsed && (
                <div style={{ fontSize: 12, color: '#8c8c8c', fontStyle: 'italic', padding: '4px 4px 0' }}>
                  No subtypes match the current threshold. Widen via the gear icon above to see more.
                </div>
              )}
              {!advisoryCollapsed && (
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #ffccc7', textAlign: 'left' }}>
                      <th style={{ padding: '4px 8px' }}>Subtype</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>n</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>1m</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>6m</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right' }}>{cur} 6m (n)</th>
                      <th style={{ padding: '4px 8px' }}>Suggested action</th>
                      <th style={{ padding: '4px 8px' }}>Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advisoryRows.map((r) => (
                      <tr key={r.alert_subtype_identifier} style={{ borderBottom: '1px solid #fce4e4' }}>
                        <td style={{ padding: '4px 8px' }}>
                          <strong>{r.alert_type}</strong>
                          <span style={{ color: '#666' }}> / {r.alert_subtype}</span>
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtN(r.n_clean)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: r.ae_p_1m_clean < 0 ? '#cf1322' : 'inherit' }}>
                          {fmtPct(r.ae_p_1m_clean)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: '#cf1322', fontWeight: 'bold' }}>
                          {fmtPct(r.ae_p_6m_clean)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                          {fmtPct(r[curValKey])} <span style={{ color: '#999' }}>({fmtN(r[curNKey])})</span>
                        </td>
                        <td style={{ padding: '4px 8px', fontSize: 11, color: '#555' }}>
                          {suggestedAction(r)}
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleIdentifierClick(r.alert_subtype_identifier);
                            }}
                            style={{ color: '#1890ff', fontSize: 11 }}
                          >Edit</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })()}

        {/* DAS-2349: Confidence × Effectiveness correlation panel.
            Bucketed bars showing realized AE_P_6m per confidence bucket,
            for two dimensions (final_confidence and weighted_pct).
            Honest about low n: buckets with n<20 are greyed.
            Hidden when there's no data at all (today; collects over time). */}
        {!overviewOrigin && correlation && (() => {
          const TOTAL_N_HIDE_BELOW = 0; // always show, even at n=0, with explanatory copy
          const total = correlation.total_alerts_evaluated || 0;
          const renderBuckets = (buckets, label) => {
            if (!buckets || !buckets.length) return null;
            const maxAbs = Math.max(
              ...buckets.map(b => Math.abs(parseFloat(b.mean_ae_p_6m) || 0)),
              5  // floor so a single small value still produces a visible bar
            );
            return (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{label}</div>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <tbody>
                    {buckets.map((b) => {
                      const isLowN = b.n < 20;
                      const v = b.mean_ae_p_6m;
                      const vNum = parseFloat(v);
                      const widthPct = (Number.isNaN(vNum) || maxAbs === 0) ? 0 : Math.abs(vNum) / maxAbs * 50; // 50% max
                      const color = isLowN ? '#ccc' : (vNum > 0 ? '#3daa6e' : '#cf1322');
                      return (
                        <tr key={b.label} style={{ opacity: isLowN ? 0.5 : 1 }}>
                          <td style={{ width: 80, padding: '2px 6px', whiteSpace: 'nowrap' }}>{b.label}</td>
                          <td style={{ width: 48, padding: '2px 6px', textAlign: 'right', color: '#888' }}>n={b.n}{isLowN ? ' ?' : ''}</td>
                          <td style={{ padding: '2px 6px' }}>
                            <div style={{ position: 'relative', height: 16, width: '100%' }}>
                              <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#999' }} />
                              {!Number.isNaN(vNum) && (
                                <div style={{
                                  position: 'absolute',
                                  left: vNum >= 0 ? '50%' : `${50 - widthPct}%`,
                                  width: `${widthPct}%`,
                                  top: 2, bottom: 2,
                                  background: color,
                                  borderRadius: 2,
                                }} />
                              )}
                            </div>
                          </td>
                          <td style={{ width: 60, padding: '2px 6px', textAlign: 'right', color, fontWeight: isLowN ? 'normal' : 'bold' }}>
                            {v === null || v === undefined ? '—' : `${vNum.toFixed(1)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          };
          return (
            <div style={{
              marginBottom: 16,
              padding: 12,
              border: '1px solid #d6e4ff',
              borderRadius: 4,
              background: '#f0f5ff',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 'bold', color: '#1d39c4' }}>
                  📊 Confidence × Effectiveness — {total} archived alerts evaluated
                  <Tooltip title="Bucketed mean AE_P_6m_clean (outliers excluded) by confidence range. Bars right of centre = positive edge over portfolio. Greyed buckets have n<20 and shouldn't be trusted yet. The dataset grows as archived alerts age into a 6m forward window — meaningful trends appear ~6 months after DAS-2349 deploy. — DAS-2349">
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help', marginLeft: 6 }} />
                  </Tooltip>
                </div>
                <Switch
                  size="small"
                  checked={!correlationCollapsed}
                  onChange={(checked) => setCorrelationCollapsed(!checked)}
                  checkedChildren="Show"
                  unCheckedChildren="Hide"
                />
              </div>
              {!correlationCollapsed && (
                total === 0 ? (
                  <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                    No archived alerts yet have BOTH preserved confidence AND a realized 6-month forward return.
                    Confidence preservation started on DAS-2349 deploy; the first meaningful trend appears
                    once those alerts age into a 6m forward window (~6 months after deploy).
                  </div>
                ) : (
                  <>
                    {renderBuckets(correlation.final_confidence_buckets, 'pf_final_confidence_score (knockout × weighted, 0–1000)')}
                    {renderBuckets(correlation.weighted_pct_buckets, 'pf_weighted_confirmation_percentage (0–100; LLM gate is at >75%)')}
                  </>
                )
              )}
            </div>
          );
        })()}

        {/* DAS-2350: Follow-Through Dashboard panel.
            Two stacked sub-panels:
              Panel 1 (Activity Tracker): per (alert_type, scope) cell over last 90 days
                — n_fired, n_seen, n_followed_up, seen_rate, followup_rate.
              Panel 2 (Hindsight Reconciliation): mean AE_P_6m_clean for alerts ≥6m old,
                split by acted-on (followed_up=1) vs ignored (followed_up=0). Empty
                until ~Nov 2026 when accumulated data ages into a 6m forward window. */}
        {!overviewOrigin && followThrough && (() => {
          const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${parseFloat(v).toFixed(1)}%`;
          const cells = followThrough.panel1 || [];
          // Build alert_type × scope grid
          const allTypes  = Array.from(new Set(cells.map(c => c.alert_type))).sort();
          // DAS-2642: order scope columns by the owner's Prime-Objective priority
          // (Portfolio → Market → Favorites → Opportunities), NOT alphabetically; show
          // the data's 'Unknown' scope under its UI name 'Opportunities'.
          const SCOPE_PRIORITY = { Portfolio: 0, Market: 1, Favorites: 2, Unknown: 3, Opportunities: 3 };
          const scopeLabel = (s) => (s === 'Unknown' ? 'Opportunities' : s);
          const allScopes = Array.from(new Set(cells.map(c => c.scope)))
            .sort((a, b) => (SCOPE_PRIORITY[a] ?? 9) - (SCOPE_PRIORITY[b] ?? 9) || a.localeCompare(b));
          const cellLookup = {};
          cells.forEach(c => { cellLookup[`${c.alert_type}|${c.scope}`] = c; });
          const totalFired = cells.reduce((sum, c) => sum + (c.n_fired || 0), 0);
          // DAS-2642: per-scope roll-up so the headline can LEAD with Portfolio,
          // instead of a blended all-scope rate that buries it behind the discovery flood.
          const byScope = {};
          cells.forEach(c => {
            const b = byScope[c.scope] || (byScope[c.scope] = { fired: 0, seen: 0, followed: 0 });
            b.fired += c.n_fired || 0; b.seen += c.n_seen || 0; b.followed += c.n_followed_up || 0;
          });
          const scopeRate = (k) => { const b = byScope[k]; return b && b.fired ? (b.followed / b.fired * 100).toFixed(1) : null; };
          const secondary = (k, label) => (byScope[k] && byScope[k].fired)
            ? ` · ${label} ${byScope[k].followed}/${byScope[k].fired} (${scopeRate(k)}%)` : '';

          const acted = followThrough.panel2?.acted_on   || { n: 0, mean_ae_p_6m: null };
          const ignored = followThrough.panel2?.ignored   || { n: 0, mean_ae_p_6m: null };
          // DAS-2871 step 2 audit: "ignored" used to include every alert that
          // was simply never triaged — 1,090 unmarked rows presented as 13
          // deliberate decisions. The unmarked majority is its own row now.
          const unmarked = followThrough.panel2?.unmarked || { n: 0, mean_ae_p_6m: null };
          const ratioAdvantage = (acted.mean_ae_p_6m !== null && ignored.mean_ae_p_6m !== null)
            ? (parseFloat(acted.mean_ae_p_6m) - parseFloat(ignored.mean_ae_p_6m)).toFixed(1)
            : null;

          return (
            <div style={{
              marginBottom: 16,
              padding: 12,
              border: '1px solid #b7eb8f',
              borderRadius: 4,
              background: '#f6ffed',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 'bold', color: '#389e0d' }}>
                  ✓ Follow-Through — last {followThrough.window_days} days
                  <span style={{ fontWeight: 'normal', color: '#333' }}>
                    {' — '}
                    {(byScope['Portfolio'] && byScope['Portfolio'].fired)
                      ? <b>Portfolio: {byScope['Portfolio'].followed} of {byScope['Portfolio'].fired} followed up ({scopeRate('Portfolio')}%)</b>
                      : <b>Portfolio: no alerts in window</b>}
                    {secondary('Market', 'Market')}
                    {secondary('Favorites', 'Favorites')}
                    {(byScope['Unknown'] && byScope['Unknown'].fired)
                      ? <span style={{ color: '#888' }}> · Opportunities/discovery {byScope['Unknown'].followed}/{byScope['Unknown'].fired} ({scopeRate('Unknown')}%)</span>
                      : null}
                  </span>
                  <Tooltip title="Follow-up is shown per scope in your priority order (Portfolio first). Portfolio/Favorites/Opportunities are per-ticker (Followed Up = you acted); Opportunities is a discovery stream — a low rate there is expected. 'Seen' auto-sets when you open a card. What ultimately matters is the Hindsight Reconciliation below: did your selectivity beat doing nothing? — DAS-2350/2642">
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help', marginLeft: 6 }} />
                  </Tooltip>
                </div>
                <Switch
                  size="small"
                  checked={!followThroughCollapsed}
                  onChange={(checked) => setFollowThroughCollapsed(!checked)}
                  checkedChildren="Show"
                  unCheckedChildren="Hide"
                />
              </div>
              {!followThroughCollapsed && (
                <>
                  {/* Panel 1: alert_type × scope grid */}
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Activity Tracker — by alert_type × scope</div>
                  {totalFired === 0 ? (
                    <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginBottom: 12 }}>
                      No alerts in the last 90 days. (Or the data hasn't been fetched yet.)
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #b7eb8f' }}>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>alert_type \ scope</th>
                            {allScopes.map((s) => (
                              <th key={s} style={{ padding: '4px 8px', textAlign: 'right' }}>{scopeLabel(s)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allTypes.map((t) => (
                            <tr key={t} style={{ borderBottom: '1px solid #d9f7be' }}>
                              <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>{t}</td>
                              {allScopes.map((s) => {
                                const c = cellLookup[`${t}|${s}`];
                                if (!c || c.n_fired === 0) {
                                  return <td key={s} style={{ padding: '4px 8px', textAlign: 'right', color: '#ccc' }}>—</td>;
                                }
                                return (
                                  <td key={s} style={{ padding: '4px 8px', textAlign: 'right' }}>
                                    <span style={{ fontWeight: 'bold' }}>{c.n_fired}</span>
                                    <span style={{ color: '#666' }}> / </span>
                                    <span title="seen_rate">{fmtPct(c.seen_rate)}</span>
                                    <span style={{ color: '#666' }}> / </span>
                                    <span title="followup_rate" style={{ color: c.followup_rate > 0 ? '#389e0d' : '#999' }}>{fmtPct(c.followup_rate)}</span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                        Cell format: <strong>n_fired</strong> / seen_rate / followup_rate
                      </div>
                    </div>
                  )}

                  {/* Panel 2: Hindsight Reconciliation */}
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Hindsight Reconciliation (alerts ≥6 months old, AE_P_6m_clean)</div>
                  {(acted.n === 0 && ignored.n === 0) ? (
                    <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                      No archived alerts ≥6 months old yet have a preserved follow-up signal.
                      Follow-up preservation started on DAS-2350 deploy; meaningful split appears
                      once those alerts age into a 6m forward window (~Nov 2026).
                    </div>
                  ) : (
                    <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #b7eb8f', textAlign: 'left' }}>
                          <th style={{ padding: '4px 8px' }}>Cohort</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right' }}>n</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right' }}>mean AE_P_6m_clean</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right' }}>mean AE_P_1m_clean</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '4px 8px' }}>Acted on (followed_up=1)</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{acted.n}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 'bold', color: acted.mean_ae_p_6m > 0 ? '#389e0d' : '#cf1322' }}>{fmtPct(acted.mean_ae_p_6m)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtPct(acted.mean_ae_p_1m)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 8px' }}>Marked as not acted on (followed_up=0)</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{ignored.n}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 'bold', color: ignored.mean_ae_p_6m > 0 ? '#389e0d' : '#cf1322' }}>{fmtPct(ignored.mean_ae_p_6m)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtPct(ignored.mean_ae_p_1m)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 8px', color: '#888' }}>Never marked either way</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>{unmarked.n}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>{fmtPct(unmarked.mean_ae_p_6m)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', color: '#888' }}>{fmtPct(unmarked.mean_ae_p_1m)}</td>
                        </tr>
                        {ratioAdvantage !== null && (
                          <tr style={{ borderTop: '1px solid #b7eb8f' }}>
                            <td colSpan={2} style={{ padding: '4px 8px', fontWeight: 'bold' }}>
                              Selectivity advantage (acted_on − ignored)
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 'bold', color: parseFloat(ratioAdvantage) >= 0 ? '#389e0d' : '#cf1322' }}>
                              {ratioAdvantage > 0 ? '+' : ''}{ratioAdvantage}%
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: 10, color: '#666' }}>
                              {parseFloat(ratioAdvantage) >= 0 ? 'gut beats indiscriminate' : 'leaving signal on the table'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* DAS-2281: Metric view tabs + search ; DAS-2346: outlier-exclusion toggle */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Radio.Group
            value={metricView}
            onChange={(e) => setMetricView(e.target.value)}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="absolute">Absolute</Radio.Button>
            <Radio.Button value="portfolio">vs Portfolio</Radio.Button>
            <Radio.Button value="degiro">vs DeGiro</Radio.Button>
            <Radio.Button value="sp500">vs S&P 500</Radio.Button>
            <Radio.Button value="hitrate">Hit Rates & Stats</Radio.Button>
          </Radio.Group>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch
              size="small"
              checked={cleanView}
              onChange={setCleanView}
              checkedChildren="Clean"
              unCheckedChildren="Raw"
              // DAS-2547: also disable when no visible row has outliers — Clean would be identical to Raw.
              disabled={metricView === 'hitrate' || noOutliersInView}
            />
            {/* DAS-2547: show the toggle's scope so it isn't a silent no-op */}
            {metricView !== 'hitrate' && (
              <span style={{ fontSize: 11, color: noOutliersInView ? '#bbb' : '#999' }}>
                {noOutliersInView
                  ? 'no outliers in view'
                  : `affects ${outlierRowCount} ${outlierRowCount === 1 ? 'type' : 'types'} (⚠️)`}
              </span>
            )}
            <Tooltip
              title={
                metricView === 'hitrate'
                  ? "Hit-rate / median / IR metrics aren't outlier-toggled in this iteration (DAS-2346 covered AE_AVG_* and AE_DG_P_* averages only). The toggle is disabled here."
                  : noOutliersInView
                    ? "Clean excludes IQR-flagged outliers from the averages, but none of the rows in this view have any — so Clean and Raw are identical and the toggle is disabled. It activates when an alert type shows a ⚠️ outlier count."
                    : `Raw includes all rows (default). Clean excludes IQR-flagged outliers (outlier=1) at aggregation time — here it changes the ${outlierRowCount} alert type(s) marked ⚠️. The n in parentheses already reflects whichever set is shown.`
              }
            >
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help' }} />
            </Tooltip>
          </div>
          {/* DAS-2347: regime filter. 'All' = regime-agnostic (Alert_Effectiveness);
              specific regime = read from Alert_Effectiveness_by_Regime. The default
              auto-snaps to the current market regime on first data load. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#666' }}>Regime:</span>
            <Radio.Group
              value={regimeFilter}
              onChange={(e) => setRegimeFilter(e.target.value)}
              size="small"
              disabled={metricView === 'hitrate'}
            >
              <Radio.Button value="All">All</Radio.Button>
              <Radio.Button value="BULL">BULL</Radio.Button>
              <Radio.Button value="NEUTRAL">NEUTRAL</Radio.Button>
              <Radio.Button value="BEAR">BEAR</Radio.Button>
            </Radio.Group>
            <Tooltip
              title={
                metricView === 'hitrate'
                  ? "Regime-stratified hit-rate / median / IR are out of scope for DAS-2347 (averages only). Disabled in this tab."
                  : `Filter the averages by the market regime in effect at alert initiation${currentRegime ? ` — current regime: ${currentRegime}` : ''}. 'All' shows the regime-agnostic averages (legacy default).`
              }
            >
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12, cursor: 'help' }} />
            </Tooltip>
          </div>
          <Input.Search
            placeholder="Search by subtype, identifier, or alert type..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
            style={{ width: 350 }}
          />
        </div>

        <Table
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          rowKey="ID"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}  // DAS-2548: horizontal scroll so wide tabs don't clip columns behind the modal edge
        />
          </>
        )}
      </Modal>
      <Modal
        title={detailModalTitle} // Dynamically set the modal title
        visible={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={1200}
      >
        <Table
          dataSource={detailedData}
          columns={detailColumns}
          rowKey="ID"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
      {/* AlertView component removed - using MarketAlerts instead */}
      <MarketAlerts
        isVisible={isMarketAlertsVisible}
        onClose={handleCloseMarketAlerts}
        alertSubtypeIdentifier={selectedAlertIdentifier}
      />
    </>
  );
};

export default AlertEffectiveness;
